/**
 * School Users API Contracts
 *
 * School user management endpoints.
 */

import { z } from 'zod'
import { PaginationMetaSchema, PaginationParamsSchema } from '../common'
import { SchoolSummarySchema } from '../../schemas/entities/school'
import { ProvisioningIssueSchema } from '../../schemas/entities/provisioning-issue'

export * from './class-grade-breakdown'
export * from './school-user-sections'

/**
 * School User Role Schema
 */
export const SchoolUserRoleSchema = z.enum(['student', 'parent', 'faculty', 'staff', 'admin', 'superAdmin'])
export type SchoolUserRole = z.infer<typeof SchoolUserRoleSchema>

/**
 * Platform Role Schema
 */
export const PlatformRoleSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional()
})

export type PlatformRole = z.infer<typeof PlatformRoleSchema>

/**
 * School User Schema
 */
export const SchoolUserSchema = z.object({
  id: z.string(),
  platformId: z.string().nullable().optional(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable().optional(),
  photoUrl: z.string().nullable().optional(),
  photoUrlThumbnail: z.string().nullable().optional(),
  roles: z
    .object({
      isStudent: z.boolean(),
      isParent: z.boolean(),
      isFaculty: z.boolean(),
      isAdmin: z.boolean(),
      isStaff: z.boolean(),
      isCandidate: z.boolean().optional(),
      isBetaTester: z.boolean()
    })
    .optional(),
  platformRoles: z.array(PlatformRoleSchema).optional(),
  school: SchoolSummarySchema.optional(),
  schoolId: z.string(),
  userId: z.string().nullable().optional(),
  gradeLevel: z.string().nullable().optional(),
  /**
   * The person's current grade at this school, as a `z_RefCoreGradeLevels`
   * public ID (`SchoolUserProfile.currentGradeLevelId` resolved through the
   * relation). Feeds the chat grade rule (`isChatGradeEligible`, SCH-1288).
   *
   * A string is the grade. `null` means no grade on record: not a student, no
   * profile row, or no grade on the profile. An ABSENT key means an older
   * server, or (single-user route) a failed grade read, and MUST be read as
   * unrestricted, never as `null`. Not the free-text `gradeLevel` above.
   */
  gradeLevelPublicId: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  lastLoginAt: z.string().nullable().optional(),
  // Parked-state marker: present (non-null) when post-OAuth provisioning failed
  // deterministically (Blackbaud SKY role-fetch 403). Omitted/null otherwise.
  // Read from `UsersOnSchools.metadata.provisioningFailed`. Drives the mobile
  // "connection issue" screen instead of the "waiting for approval" copy.
  provisioningIssue: ProvisioningIssueSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string()
})

export type SchoolUser = z.infer<typeof SchoolUserSchema>

/**
 * GET /v1/school-users
 */
export const GetSchoolUsersRequestSchema = PaginationParamsSchema.extend({
  schoolId: z.string().optional(),
  roles: z.string().optional(),
  search: z.string().optional()
})

export type GetSchoolUsersRequest = z.infer<typeof GetSchoolUsersRequestSchema>

export const GetSchoolUsersResponseSchema = z.object({
  data: z.array(SchoolUserSchema),
  meta: PaginationMetaSchema.optional(),
  message: z.string()
})

export type GetSchoolUsersResponse = z.infer<typeof GetSchoolUsersResponseSchema>

/**
 * GET /v1/school-users/current
 *
 * NOTE: the `/current` endpoint returns a richer, distinct payload from the
 * generic `SchoolUserSchema` list row above — the school is nested under
 * `data.school` (there is no top-level `schoolId`), names are nullable, there
 * are no `createdAt`/`updatedAt` timestamps, and it carries relationship,
 * platform, app-user and rewards detail. This schema mirrors the backend's
 * `formatCurrentSchoolUserResponse` output
 * (apps/backend/src/api/users/(school-users)/v1/use-cases/current/schema.ts).
 * Keep the two shapes in sync; do NOT reuse `SchoolUserSchema` here.
 */
export const CurrentSchoolUserRolesSchema = z.object({
  isStudent: z.boolean(),
  isParent: z.boolean(),
  isFaculty: z.boolean(),
  isAdmin: z.boolean(),
  isStaff: z.boolean(),
  isCandidate: z.boolean().optional(),
  isBetaTester: z.boolean()
})

export const CurrentSchoolUserRelationshipIndexSchema = z.object({
  childIds: z.array(z.string()),
  parentIds: z.array(z.string()),
  studentIds: z.array(z.string()),
  teacherIds: z.array(z.string()),
  enrolledSectionIds: z.array(z.string()),
  taughtSectionIds: z.array(z.string()),
  /**
   * Parents only: section publicIds where one of the viewer's children is an
   * ACTIVE student (SCH-1656).
   *
   * OPTIONAL on purpose, following `isCandidate` above: this is an additive
   * field, and the contract is parsed against whatever backend the client
   * actually reaches — prod until this ships, and any device left on an older
   * build afterwards. Pinning it as required made the prod boot smoke suite
   * fail the whole `/v1/school-users/current` parse on a field prod does not
   * emit yet, which is the same way a client would fail against an older
   * backend. The backend always emits it; consumers must treat `undefined` as
   * "no sections", which is the fail-closed direction the resolver already
   * takes.
   */
  childSectionIds: z.array(z.string()).optional()
})

/**
 * Session-payload group element (`GET /v1/school-users/current` → `data.groups[]`).
 *
 * Mirrors what `formatCurrentSchoolUserResponse` emits, but only PINS the fields a
 * client contract depends on today — `id`, `isSystemGroup`, and the nested
 * `membership.permissions.canPost` (the mobile FAB's Group-Post gate). The
 * serializer's other `...group` fields still ride the wire; they're simply not
 * part of this pinned contract.
 *
 * NOTE: deliberately NOT `.passthrough()`. This is a closed shape on purpose: its
 * inferred `[k: string]: unknown` index signature (which passthrough adds) would
 * make mobile's concrete `Group` interface (no index signature) un-assignable to
 * the inferred `SessionGroup`, defeating the whole point of a TYPED FAB gate.
 *
 * IMPORTANT: `GetCurrentSchoolUserResponseSchema` / `CurrentSchoolUserDataSchema`
 * ARE parsed at runtime — the mobile-api-test boot suite `safeParse`s the live
 * `GET /v1/school-users/current` response against `GetCurrentSchoolUserResponseSchema`
 * (see `tools/mobile-api-test/src/suites/boot.ts` + `runner.ts`). They are NOT a
 * documentation-only shape: tightening or renaming a field here can fail that boot
 * smoke test, so verify the real serializer output before changing them.
 */
export const SessionGroupMembershipPermissionsSchema = z.object({
  canPost: z.boolean()
})

export const SessionGroupMembershipSchema = z.object({
  permissions: SessionGroupMembershipPermissionsSchema.nullable().optional()
})

export const SessionGroupSchema = z.object({
  id: z.string(),
  isSystemGroup: z.boolean(),
  membership: SessionGroupMembershipSchema.nullable().optional()
})

export type SessionGroup = z.infer<typeof SessionGroupSchema>

/**
 * Opaque Blackbaud platform passthrough (`data.platform.roles` / `data.platform.directories`).
 * At runtime `roles` is a raw BB role array and `directories` a raw BB directory object
 * (`{ access: [...] }`); the backend forwards both untyped and their shapes drift with BB.
 * Typed as `unknown` (never `z.any()`, SCH-444) so nearby code can't treat the payload as
 * validated — consumers MUST narrow before use. Do NOT tighten to a strict schema: it must
 * never reject a real boot payload.
 */
export const PlatformPassthroughSchema = z.unknown()

/**
 * Session-payload relationship element (`data.relationships[]`). Mirrors the backend
 * serializer's relationship shape (`formatCurrentSchoolUserResponse` → `RelationshipSchema`)
 * but is intentionally lenient — only the always-present `id`/`myRole`/`theirRole` are
 * required, everything else optional/nullable and unknown keys are stripped (never rejected).
 * A documentation shape, not a runtime gate (SCH-444).
 */
export const SessionRelationshipSchema = z.object({
  id: z.string(),
  myRole: z.string(),
  theirRole: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  platformId: z.string().nullable().optional(),
  preferredFirstName: z.string().nullable().optional(),
  preferredLastName: z.string().nullable().optional(),
  photoUrlThumbnail: z.string().nullable().optional(),
  photoUrlLarge: z.string().nullable().optional(),
  enrollmentStatus: z.enum(['ACTIVE', 'DEPARTED', 'UNKNOWN']).optional(),
  enrollDate: z.string().nullable().optional(),
  departDate: z.string().nullable().optional(),
  isStudent: z.boolean().optional(),
  /**
   * The child's CURRENT SCHOOL LEVEL publicId (`SchoolUserProfile.currentSchoolLevelId`
   * resolved through the relation), or `null` when the child has no profile row or the
   * column is unset. Only present for CHILD relationships.
   *
   * This is the level-keyed config's key — the same id `attendance.levelConfigs` and
   * `attendance.parentAbsence.reasonsByLevel` are keyed by — so a client can resolve a
   * child's allowed reasons WITHOUT the opportunistically-loaded directory listing.
   *
   * FAIL OPEN on `null`: a child whose level does not resolve must still be SHOWN and
   * still be reportable (~3,920 students fleet-wide have no profile row at all).
   * Blackbaud remains the backstop; hiding the child would dead-end a real parent.
   */
  schoolLevelId: z.string().nullable().optional()
})

export type SessionRelationship = z.infer<typeof SessionRelationshipSchema>

/**
 * Session-payload followed-feed element (`data.feedsFollowed[]`). The backend emits
 * `{ feed_id, feed_type }` (snake_case) per followed timeline/notification feed. `feed_type`
 * is left a free string so a new feed type never rejects a boot payload (SCH-444).
 */
export const SessionFeedFollowSchema = z.object({
  feed_id: z.string(),
  feed_type: z.string()
})

export type SessionFeedFollow = z.infer<typeof SessionFeedFollowSchema>

export const CurrentSchoolUserDataSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  preferredFirstName: z.string().nullable(),
  preferredLastName: z.string().nullable(),
  maidenName: z.string().nullable(),
  displayName: z.string().nullable(),
  photoUrlThumbnail: z.string().nullable(),
  photoUrlLarge: z.string().nullable(),
  platformId: z.string().nullable(),
  status: z.string().nullable(),
  // Parked-state marker (see SchoolUserSchema.provisioningIssue). Present when
  // this membership is parked on a deterministic provisioning failure; the
  // mobile parked screen keys on this marker, not on `status`.
  provisioningIssue: ProvisioningIssueSchema.nullable().optional(),
  roles: CurrentSchoolUserRolesSchema,
  platform: z.object({
    // Opaque platform passthrough — `roles` is an array, `directories` is an
    // object ({ access: [...] }) at runtime; the backend forwards both untyped.
    // Named PlatformPassthroughSchema (`unknown`) so agents don't copy `z.any()` here.
    roles: PlatformPassthroughSchema.nullable().optional(),
    directories: PlatformPassthroughSchema.nullable().optional(),
    lastSyncedAt: z.string().nullable()
  }),
  relationships: z.array(SessionRelationshipSchema).nullable().optional(),
  relationshipIndex: CurrentSchoolUserRelationshipIndexSchema,
  groups: z.array(SessionGroupSchema).nullable().optional(),
  /**
   * Can this user publish to at least one space at the ACTIVE school? (plan
   * 2026-08-08-003, U1 / R1-R4.) The mobile action button's publishing gate.
   *
   * ONE authoritative per-user answer, derived server-side from the canonical
   * posting resolver and reduced to the grant tiers the write path can actually
   * read. It is NOT "does the group list contain a postable row" — that list is
   * filtered by a legacy visibility column that is false for most section
   * leaders, which is the bug this replaces. Clients must read this field and
   * must NOT re-derive the answer from `groups[]`.
   *
   * A provable SUBSET of what the write path accepts: it under-shows for
   * populations whose only posting route is a tier the write path cannot read
   * (the "members may post" setting on either space kind; leader and
   * moderator-grant on a COMMUNITY space). Under-showing is the deliberate
   * direction to fail — a user who briefly lacks the button recovers, a user who
   * posts into a rejection does not.
   *
   * Optional on the wire so a client on an older backend parses; absent MUST be
   * read as `false`, never as truthy.
   */
  canPostAnywhere: z.boolean().optional(),
  /**
   * The caller's current grade at the ACTIVE school, as a `z_RefCoreGradeLevels`
   * public ID (SCH-1288). Feeds the chat grade rule (`isChatGradeEligible`).
   *
   * A string is the grade. `null` means no grade on record: not a student at the
   * active school, no profile row, or no grade on the profile. An ABSENT key
   * means an older server or a failed grade read, and MUST be read as
   * unrestricted, never as `null` (the opposite absent rule to `canPostAnywhere`).
   * Failing open here only affects the UI: the server-side chat create/add
   * checks still enforce the grade rule.
   */
  gradeLevelPublicId: z.string().nullable().optional(),
  school: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      slug: z.string().nullable()
    })
    .nullable(),
  appUser: z
    .object({
      id: z.string(),
      authProviderId: z.string().nullable(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      email: z.string().nullable()
    })
    .nullable(),
  appConfig: z
    .object({
      homeScreen: z.object({
        // Left as `unknown` (not `any`): the backend currently always emits `null`
        // here, so no concrete announcement element shape is exercised on this path
        // yet. Type the element against the announcement entity schema once it does.
        announcements: z.array(z.unknown()).nullable()
      })
    })
    .nullable(),
  feedsFollowed: z.array(SessionFeedFollowSchema).nullable().optional(),
  rewards: z
    .object({
      points: z.object({
        current: z.number(),
        lifetimeEarned: z.number(),
        lifetimeSpent: z.number()
      }),
      achievements: z.object({
        badgesEarned: z.number(),
        journeysCompleted: z.number(),
        journeysInProgress: z.number()
      })
    })
    .nullable()
})

export const GetCurrentSchoolUserResponseSchema = z.object({
  data: CurrentSchoolUserDataSchema,
  message: z.string()
})

export type GetCurrentSchoolUserResponse = z.infer<typeof GetCurrentSchoolUserResponseSchema>

/**
 * GET /v1/school-users/:userId
 */
export const GetSchoolUserRequestSchema = z.object({
  userId: z.string(),
  platform_roles: z.boolean().optional()
})

export type GetSchoolUserRequest = z.infer<typeof GetSchoolUserRequestSchema>

export const GetSchoolUserResponseSchema = z.object({
  data: SchoolUserSchema,
  message: z.string()
})

export type GetSchoolUserResponse = z.infer<typeof GetSchoolUserResponseSchema>

/**
 * PATCH /v1/school-users/:userId
 */
export const UpdateSchoolUserRequestSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  roles: z.array(z.string()).optional(),
  department: z.string().optional(),
  title: z.string().optional(),
  isActive: z.boolean().optional(),
  /**
   * School Journey app admin for this membership (`UsersOnSchools.isAdmin`) —
   * the per-user switch beside Beta Tester in the user detail dialog.
   *
   * The backend REFUSES a write that would leave the school with zero ACTIVE
   * admins, and returns error type `LAST_ACTIVE_ADMIN` (HTTP 409) rather than a
   * generic update failure, so clients can say "this is the last admin".
   */
  isAdmin: z.boolean().optional()
})

export type UpdateSchoolUserRequest = z.infer<typeof UpdateSchoolUserRequestSchema>

export const UpdateSchoolUserResponseSchema = z.object({
  data: SchoolUserSchema,
  message: z.string()
})

export type UpdateSchoolUserResponse = z.infer<typeof UpdateSchoolUserResponseSchema>

/**
 * GET /v1/school-users/:userId/data-connection
 */
export const GetUserDataConnectionResponseSchema = z.object({
  data: z
    .object({
      id: z.string(),
      schoolUserId: z.string(),
      platformId: z.string().nullable(),
      status: z.string(),
      lastSyncedAt: z.string().nullable()
    })
    .nullable(),
  message: z.string()
})

export type GetUserDataConnectionResponse = z.infer<typeof GetUserDataConnectionResponseSchema>

/**
 * GET /v1/schools/:schoolId/school-users/:schoolUserPublicId/feeds
 */
export const GetUserFeedsResponseSchema = z.object({
  data: z.object({
    timeline: z
      .object({
        feedId: z.string(),
        activityCount: z.number().optional()
      })
      .optional(),
    notification: z
      .object({
        feedId: z.string(),
        unseenCount: z.number().optional()
      })
      .optional(),
    groups: z
      .array(
        z.object({
          groupId: z.string(),
          groupName: z.string(),
          feedId: z.string()
        })
      )
      .optional()
  }),
  message: z.string()
})

export type GetUserFeedsResponse = z.infer<typeof GetUserFeedsResponseSchema>

/**
 * GET /v1/school-users/current/sections
 * Retrieve sections (classes, activities, athletics) for the current authenticated school user
 */

/**
 * Section schema representing a user's enrolled section
 */
export const SectionSchema = z.object({
  /** Section public ID */
  id: z.string(),
  /** Section name */
  name: z.string(),
  /** Display name for the section */
  displayName: z.string(),
  /** Section description */
  description: z.string().nullable(),
  /** Group type identifier */
  groupType: z.string(),
  /** Data source (e.g., 'BLACKBAUD', 'MANUAL') */
  source: z.string(),
  /** Offering type (ACADEMIC, ATHLETICS, etc.) */
  offeringType: z.string().nullable(),
  /** Course code */
  courseCode: z.string().nullable(),
  /** School level (e.g., 'Middle School', 'Upper School') */
  schoolLevel: z.string().nullable(),
  /** Academic year (e.g., '2025-2026') */
  schoolYear: z.string().nullable(),
  /** Duration name (e.g., 'Fall', 'Spring', 'Full Year') */
  durationName: z.string().nullable(),
  /** Room name */
  roomName: z.string().nullable(),
  /** Venue name */
  venueName: z.string().nullable(),
  /** Season name (for athletics) */
  seasonName: z.string().nullable(),
  /** Total person count */
  personCount: z.number().nullable(),
  /** When this section was last synced from external system */
  lastSyncedAt: z.string().nullable(),
  /** Number of members in the section */
  memberCount: z.number(),
  /** Whether this is a system-managed group */
  isSystemGroup: z.boolean(),
  /** Current user's membership details */
  myMembership: z
    .object({
      /** Membership public ID */
      membershipPublicId: z.string(),
      /** Permission level */
      permissionLevel: z.string(),
      /** Identity role (e.g., 'STUDENT', 'FACULTY') */
      identityRole: z.string(),
      /** Role within section (e.g., 'TEACHER', 'STUDENT') */
      sectionRole: z.string(),
      /** Whether user is a leader */
      isLeader: z.boolean(),
      /** Whether user has head role */
      isHeadRole: z.boolean(),
      /** Membership status */
      status: z.string(),
      /** When user joined this section */
      joinedAt: z.string()
    })
    .nullable()
})

export type Section = z.infer<typeof SectionSchema>

export const GetCurrentUserSectionsRequestSchema = z.object({
  /**
   * Filter by offering type. Values MUST match the backend canonical
   * `OfferingType` union (services/platform-entities/sections/shared/types.ts):
   * the advisory value is `ADVISORIES` (plural) — the DB stores that literal, so
   * the singular `ADVISORY` silently matched zero rows (SCH-443).
   */
  offering_type: z.enum(['ACADEMIC', 'ATHLETICS', 'ADVISORIES', 'ACTIVITIES', 'COMMUNITY_GROUPS']).optional(),
  /** Filter by school level */
  school_level: z.string().optional(),
  /** Filter by school year */
  school_year: z.string().optional(),
  /** Maximum number of sections to return (default: 100) */
  limit: z.coerce.number().optional(),
  /** Number of sections to skip for pagination (default: 0) */
  offset: z.coerce.number().optional()
})

export type GetCurrentUserSectionsRequest = z.infer<typeof GetCurrentUserSectionsRequestSchema>

export const GetCurrentUserSectionsResponseSchema = z.object({
  data: z.array(SectionSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number()
  }),
  message: z.string()
})

export type GetCurrentUserSectionsResponse = z.infer<typeof GetCurrentUserSectionsResponseSchema>
export * from './search'
export * from './user-detail'
